import { Component, DestroyRef, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { filter, fromEvent, map, merge, startWith, tap } from 'rxjs';
import { HeaderElementInterface } from '../../interfaces/header-element.interface';

@Component({
  selector: 'app-navigation',
  imports: [TranslateModule, MatTooltipModule],
  templateUrl: './navigation.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './navigation.component.scss'
})
export class NavigationComponent implements OnInit {
  private readonly router: Router = inject(Router);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  protected navElemSelected = -1;
  protected readonly yOffset = -68;
  protected sliderWidth = '0px';
  protected sliderOffset = '0px';

  protected headerElements: HeaderElementInterface[] = [
    {
      displayName: 'navigation.header-title',
      elemId: 'home'
    },
    {
      displayName: 'navigation.help-items',
      elemId: 'help-topics'
    },
    {
      displayName: 'navigation.important-links',
      elemId: 'important-links'
    },
    {
      displayName: 'navigation.faq',
      elemId: 'faq'
    }
  ];

  protected isOnHome = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      tap(() => this.initSelectedElemOnNavigation()),
      map(() => this.getFirstPathSegment() === 'home'),
      startWith(this.getFirstPathSegment() === 'home')
    ),
    { initialValue: this.getFirstPathSegment() === 'home' }
  );

  protected isMobile: WritableSignal<boolean> = signal<boolean>(false);

  ngOnInit(): void {
    this.listenToWindowEvents();
  }

  protected onItemClick(event: MouseEvent, elemId: string): void {
    event.preventDefault();

    if (!this.isOnHome()) {
      void this.router.navigate(['home']).then(() => {
        setTimeout(() => this.scrollToElem(elemId), 0);
      });
    } else {
      this.scrollToElem(elemId);
    }
  }

  private initSelectedElemOnNavigation(): void {
    if (this.isOnHome()) {
      this.navElemSelected = 0;
    } else if (this.router.url.startsWith('/help-topics')) {
      this.navElemSelected = 1;
    } else if (this.router.url.startsWith('/faq')) {
      this.navElemSelected = 3;
    } else {
      this.navElemSelected = -1;
    }
    setTimeout(() => {
      this.updateSlider();
    }, 0);
  }

  private scrollToElem(elemId: string): void {
    const element = document.querySelector(`#${elemId}`);
    if (!element) {
      return;
    }
    const y = element.getBoundingClientRect().top + window.pageYOffset + this.yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }

  private calculateCurrentSelectedElem(): number {
    for (let i = 0; i < this.headerElements.length; i++) {
      const elem = this.headerElements[i];
      const element = document.querySelector(`#${elem.elemId}`);
      if (!element) {
        continue;
      }
      const y = element.getBoundingClientRect().top + window.pageYOffset;
      if (y > window.scrollY) {
        return i;
      }
    }
    return this.headerElements.length - 1;
  }

  private updateSlider(): void {
    if (this.navElemSelected === -1) {
      this.sliderWidth = '0px';
      return;
    }

    const elem = this.headerElements[this.navElemSelected];
    const element = document.querySelector(`.nav-${elem.elemId}`);

    if (element) {
      const width = element.getBoundingClientRect().width;
      this.sliderWidth = `${width}px`;

      // As the slider's absolute position should be given relative to the navbar, but the x is relative to the window
      // We need to do x - parentLeft to find the position relative to the navbar
      const x = element.getBoundingClientRect().left;
      const parentLeft = element.parentElement.getBoundingClientRect().left;
      this.sliderOffset = `${x - parentLeft}px`;
    }
  }

  private listenToWindowEvents(): void {
    merge(fromEvent(window, 'scroll'), fromEvent(window, 'resize'))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.recalculateSelectedElement())
      )
      .subscribe();
  }

  private recalculateSelectedElement(): void {
    if (!this.isOnHome()) {
      return;
    }
    this.navElemSelected = this.calculateCurrentSelectedElem();
    this.updateSlider();
  }

  private getFirstPathSegment(): string {
    const urlTree = this.router.parseUrl(this.router.url);
    return urlTree.root.children.primary?.segments[0]?.path || '';
  }
}
